import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { documentsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { DocumentDetailsDto } from '@shared/contracts';
import {
  ArrowLeft,
  Eye,
  CheckCircle,
  Download,
  Sliders,
  Loader2,
  Globe,
} from 'lucide-react';

export const DocumentViewerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [docData, setDocData] = useState<DocumentDetailsDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [redactionMode, setRedactionMode] = useState<string>('BLACK_BOX');
  const [applying, setApplying] = useState(false);
  const [activeTab, setActiveTab] = useState<'text' | 'preview'>('text');
  const [selectedEntityTypes, setSelectedEntityTypes] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [previewVersion, setPreviewVersion] = useState<number>(Date.now());

  const authToken = token || localStorage.getItem('sensiavera_token') || '';
  const fileUrl = id ? `/api/documents/${id}/file?token=${encodeURIComponent(authToken)}&_v=${previewVersion}` : '';

  const fetchDoc = async (initSelections: boolean = false) => {
    if (!id) return;
    try {
      const res = await documentsApi.getById(id);
      setDocData(res);
      if (initSelections) {
        const allTypes: string[] = Array.from(
          new Set((res?.detections || []).map((d) => d.entityType || d.name || 'UNSPECIFIED'))
        );
        setSelectedEntityTypes(allTypes);
      }
    } catch (e) {
      console.error('Failed to load document details', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoc(true);
  }, [id]);

  const handleToggleEntityType = (type: string) => {
    if (selectedEntityTypes.includes(type)) {
      setSelectedEntityTypes(selectedEntityTypes.filter((t) => t !== type));
    } else {
      setSelectedEntityTypes([...selectedEntityTypes, type]);
    }
  };

  const handleApplyRedaction = async () => {
    if (!docData || !id) return;
    setApplying(true);
    setMessage('');
    try {
      const filteredItems = (docData.detections || []).filter((d) =>
        selectedEntityTypes.includes(d.entityType || d.name || '')
      );
      await documentsApi.redact(id, filteredItems, redactionMode);
      setMessage('Redaction applied successfully!');
      setPreviewVersion(Date.now());
      await fetchDoc(false);
      setActiveTab('preview');
    } catch (e) {
      setMessage('Redaction error: ' + (e instanceof Error ? e.message : 'Redaction failed'));
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="py-24 text-center space-y-4">
        <Loader2 className="w-8 h-8 animate-spin text-sky-600 mx-auto" />
        <p className="text-slate-500 text-xs font-medium">Loading document viewer...</p>
      </div>
    );
  }

  if (!docData) {
    return (
      <div className="py-20 text-center space-y-4">
        <p className="text-red-600 text-sm font-semibold">Document not found.</p>
        <Link to="/documents" className="text-sky-600 text-xs hover:underline">
          Return to Documents Workspace
        </Link>
      </div>
    );
  }

  const { document: doc, detections } = docData;

  const entityBadgesColor: Record<string, string> = {
    GERMAN_TAX_ID: 'bg-amber-50 text-amber-700 border-amber-200',
    FRENCH_INSEE: 'bg-blue-50 text-blue-700 border-blue-200',
    UK_NINO: 'bg-sky-50 text-sky-700 border-sky-200',
    ITALIAN_CODICE_FISCALE: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    SPANISH_DNI: 'bg-orange-50 text-orange-700 border-orange-200',
    DUTCH_BSN: 'bg-amber-50 text-amber-700 border-amber-200',
    SWEDISH_PERSONAL_NUMBER: 'bg-sky-50 text-sky-700 border-sky-200',
    US_SSN: 'bg-red-50 text-red-700 border-red-200',
    CANADIAN_SIN: 'bg-pink-50 text-pink-700 border-pink-200',
    SINGAPORE_NRIC_FIN: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    CHINESE_RESIDENT_ID: 'bg-rose-50 text-rose-700 border-rose-200',
    AUSTRALIAN_TFN: 'bg-purple-50 text-purple-700 border-purple-200',
    NATIONAL_ID: 'bg-red-50 text-red-700 border-red-200',
    IBAN: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };

  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Top Bar */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 bg-white p-5 rounded-2xl border border-slate-200 shadow-sm">
        <div className="flex items-center gap-3">
          <Link
            to="/documents"
            className="p-2 rounded-xl bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-900 transition-colors border border-slate-200"
          >
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold text-slate-900 tracking-tight">{doc.originalName || doc.fileName}</h1>
              <span className="px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                {doc.status}
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-0.5">
              <span className="flex items-center gap-1 text-slate-700 font-medium">
                <Globe className="w-3.5 h-3.5 text-sky-600 shrink-0" />
                {doc.countryName || 'United States'} ({doc.countryIso || 'US'})
              </span>
              <span>• Regulation: <strong className="text-slate-800">{doc.privacyRegulation || 'GDPR'}</strong></span>
              <span>• Category: <strong className="text-slate-800">{doc.documentCategory || 'IDENTITY'}</strong></span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          {doc.redactedStoragePath && (
            <a
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              className="w-full sm:w-auto px-4 py-2 rounded-xl bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-200 text-xs font-semibold flex items-center justify-center gap-2 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Download Redacted File</span>
            </a>
          )}
        </div>
      </div>

      {message && (
        <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-xs font-medium flex items-center gap-2">
          <CheckCircle className="w-4 h-4" />
          <span>{message}</span>
        </div>
      )}

      {/* Main Dual-Pane Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Pane: Document Text / Live Preview */}
        <div className="lg:col-span-8 bg-white p-4 sm:p-6 rounded-2xl border border-slate-200 shadow-sm space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-100 pb-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveTab('text')}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                  activeTab === 'text'
                    ? 'bg-sky-50 text-sky-700 border border-sky-200'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Extracted Text
              </button>
              <button
                onClick={() => setActiveTab('preview')}
                className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${
                  activeTab === 'preview'
                    ? 'bg-sky-50 text-sky-700 border border-sky-200'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                Document Preview
              </button>
            </div>

            <div className="text-xs text-slate-500 font-medium">
              {detections?.length || 0} PII Entities Found
            </div>
          </div>

          {activeTab === 'text' ? (
            <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 font-mono text-xs text-slate-800 leading-relaxed max-h-[350px] sm:max-h-[550px] overflow-y-auto whitespace-pre-wrap">
              {doc.rawText || 'No raw text extracted.'}
            </div>
          ) : (
            <div className="h-[350px] sm:h-[500px] lg:h-[600px] w-full rounded-xl overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center">
              {doc.mimeType?.startsWith('image/') ? (
                <img
                  key={previewVersion}
                  src={fileUrl}
                  alt={doc.originalName || 'Document Preview'}
                  className="max-h-full max-w-full object-contain p-2"
                />
              ) : (
                <iframe
                  key={previewVersion}
                  src={fileUrl}
                  className="w-full h-full border-none"
                  title="Document Preview"
                />
              )}
            </div>
          )}
        </div>

        {/* Right Pane: Redaction Controls */}
        <div className="lg:col-span-4 bg-white p-4 sm:p-6 rounded-2xl border border-slate-200 shadow-sm space-y-6 flex flex-col justify-between">
          <div className="space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-100 pb-3">
              <Sliders className="w-5 h-5 text-sky-600" />
              <h2 className="text-base font-bold text-slate-900">Redaction Settings</h2>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700">Redaction Overlay Style</label>
              <select
                value={redactionMode}
                onChange={(e) => setRedactionMode(e.target.value)}
                className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-sky-500"
              >
                <option value="BLACK_BOX">Solid Black Box</option>
                <option value="GAUSSIAN_BLUR">Gaussian Blur Overlay</option>
                <option value="PIXELATION">Pixelation Mask</option>
              </select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-semibold text-slate-700">Target Entities</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setSelectedEntityTypes(
                        Array.from(new Set(detections.map((d) => d.entityType || d.name || 'UNSPECIFIED'))),
                      )
                    }
                    className="text-[11px] font-semibold text-sky-600 hover:text-sky-700 cursor-pointer"
                  >
                    Select All
                  </button>
                  <span className="text-slate-300">•</span>
                  <button
                    type="button"
                    onClick={() => setSelectedEntityTypes([])}
                    className="text-[11px] font-semibold text-slate-500 hover:text-slate-700 cursor-pointer"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {Array.from(new Set(detections.map((d) => d.entityType || d.name || 'UNSPECIFIED'))).map((type) => (
                  <label
                    key={type}
                    className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 border border-slate-200 hover:border-slate-300 cursor-pointer transition-all"
                  >
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedEntityTypes.includes(type)}
                        onChange={() => handleToggleEntityType(type)}
                        className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      />
                      <span
                        className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${
                          entityBadgesColor[type] || 'bg-slate-100 text-slate-700 border-slate-200'
                        }`}
                      >
                        {type}
                      </span>
                    </div>
                    <span className="text-[11px] font-medium text-slate-500">
                      {detections.filter((d) => (d.entityType || d.name) === type).length} found
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <button
            onClick={handleApplyRedaction}
            disabled={applying}
            className="w-full py-2.5 rounded-xl btn-primary text-xs font-semibold shadow-sm flex items-center justify-center gap-2"
          >
            {applying ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Applying...</span>
              </>
            ) : (
              <>
                <Eye className="w-4 h-4" />
                <span>Apply & Burn Redaction</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

